import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Upload, message, Spin, InputNumber, Radio, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useUpdateEventMutation } from '@/services/api/event/eventApi';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';
import "./eventModal.css"

const { TextArea } = Input;

const EditEventModal = ({
  visible,
  onCancel,
  onSuccess,
  eventData
}) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [servingType, setServingType] = useState('range');
  const [uploading, setUploading] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const [updateEvent, { isLoading }] = useUpdateEventMutation();

  useEffect(() => {
    if (eventData && visible) {
      const isFixed = eventData.minimum_serving_size === eventData.maximum_serving_size;
      setServingType(isFixed ? 'fixed' : 'range');
      
      const activeStatus = eventData.status === 1 || eventData.status === "Active";
      setIsActive(activeStatus);

      form.setFieldsValue({
        name: eventData.name,
        description: eventData.description,
        minimum_serving_size: eventData.minimum_serving_size || 0,
        maximum_serving_size: eventData.maximum_serving_size || 0,
        fixed_serving_size: isFixed ? eventData.minimum_serving_size || 0 : undefined,
        active: activeStatus,
      });
      setImageUrl(eventData.url || '');
    }
  }, [eventData, visible, form]);

  const handleUpload = async (file) => {
    try {
      setUploading(true);
      message.loading({ content: 'Uploading image...', key: 'upload' });
      const url = await uploadToCloudinary(file);
      setImageUrl(url);
      message.success({ content: 'Image uploaded successfully!', key: 'upload' });
    } catch (error) {
      console.error('Cloudinary upload failed:', error);
      message.error({ content: 'Failed to upload image', key: 'upload' });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadChange = async ({ file }) => {
    if (file.status !== 'removed' && file.originFileObj) {
      await handleUpload(file.originFileObj);
    } else if (file.status === 'removed') {
      setImageUrl('');
    }
  };

  const handleActiveChange = (checked) => {
    setIsActive(checked);
    form.setFieldsValue({ active: checked });
  };

  const handleSubmit = async (values) => {
    if (!eventData?.id) {
      message.error('Package ID is missing');
      return;
    }

    setIsSubmitting(true);
    try {
      let minimumServingSize, maximumServingSize;

      if (servingType === 'fixed') {
        minimumServingSize = values.fixed_serving_size || 0;
        maximumServingSize = values.fixed_serving_size || 0;
      } else {
        minimumServingSize = values.minimum_serving_size || 0;
        maximumServingSize = values.maximum_serving_size || 0;
      }

      const payload = {
        name: values.name,
        minimum_serving_size: minimumServingSize,
        maximum_serving_size: maximumServingSize,
        url: imageUrl || '',
        status: values.active ? 1 : 0,
        description: values.description
      };

      await updateEvent({
        id: eventData.id,
        requestData: payload
      }).unwrap();

      message.success('Package updated successfully!');
      form.resetFields();
      setImageUrl('');
      setServingType('range');
      setIsActive(false);
      onSuccess && onSuccess();
      onCancel();
    } catch (error) {
      console.error('Error updating package:', error);
      message.error(error?.data?.message || 'Failed to update package');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setImageUrl('');
    setServingType('range');
    setIsActive(false);
    onCancel();
  };

  if (!eventData) return null;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
      closable={false}
      centered
      className="edit-package-modal"
    >
      {/* Fixed Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800">Edit Package</h2>
            <p className="text-sm text-gray-500 mt-1">Update package details and information</p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          >
            <CloseOutlined className="text-lg" />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto px-6 py-6" style={{ maxHeight: '70vh' }}>
        <Spin spinning={isSubmitting || isLoading || uploading}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} className="space-y-5">
            
            {/* Package Status */}
            <Form.Item 
              name="active" 
              label={<span className="text-gray-700 font-medium text-base">Package Status</span>} 
              valuePropName="checked"
            >
              <div className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <Switch 
                  checkedChildren="ACTIVE" 
                  unCheckedChildren="INACTIVE" 
                  onChange={handleActiveChange} 
                  checked={isActive} 
                  style={{ backgroundColor: isActive ? '#52c41a' : '#d9d9d9' }} 
                />
                <div className="flex flex-col">
                  <span className={`text-sm font-medium ${isActive ? 'text-green-700' : 'text-gray-600'}`}>
                    {isActive ? 'Package is Active' : 'Package is Inactive'}
                  </span>
                  <span className={`text-xs ${isActive ? 'text-green-600' : 'text-gray-500'}`}>
                    {isActive ? 'Package will be available to customers' : 'Package is hidden from customers'}
                  </span>
                </div>
              </div>
            </Form.Item>

            {/* Package Name */}
            <Form.Item 
              name="name" 
              label={<span className="text-gray-700 font-medium">Package Name<span className="text-red-500 ml-1">*</span></span>} 
              rules={[
                { required: true, message: 'Please enter package name' },
                { min: 2, message: 'Name must be at least 2 characters' },
                { max: 100, message: 'Name must be less than 100 characters' }
              ]}
            >
              <Input 
                placeholder="e.g. Native Essence" 
                size="large" 
                className="h-11 rounded-lg" 
              />
            </Form.Item>

            {/* Description */}
            <Form.Item 
              name="description" 
              label={<span className="text-gray-700 font-medium">Description<span className="text-red-500 ml-1">*</span></span>} 
              rules={[
                { required: true, message: 'Please enter description' },
                { min: 2, message: 'Description must be at least 2 characters' },
                { max: 500, message: 'Description must be less than 500 characters' }
              ]}
            >
              <TextArea 
                placeholder="Describe the package" 
                rows={3} 
                size="large" 
                className="rounded-lg" 
              />
            </Form.Item>

            {/* Number of Guests */}
            <div>
              <label className="text-gray-700 font-medium mb-3 block">
                Number of Guests <span className="text-red-500">*</span>
              </label>
              
              <Radio.Group 
                value={servingType} 
                onChange={(e) => setServingType(e.target.value)} 
                className="mb-3"
              >
                <Radio value="range">Range</Radio>
                <Radio value="fixed" className="ml-6">Fixed</Radio>
              </Radio.Group>

              {servingType === 'range' ? (
                <div className="flex items-center gap-3">
                  <Form.Item 
                    name="minimum_serving_size" 
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 0, message: 'Must be 0 or greater' }
                    ]} 
                    className="mb-0"
                  >
                    <InputNumber 
                      placeholder="0" 
                      size="large" 
                      className="w-32 h-11 rounded-lg" 
                      min={0} 
                    />
                  </Form.Item>
                  
                  <span className="text-gray-500 font-medium">to</span>
                  
                  <Form.Item 
                    name="maximum_serving_size" 
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'number', min: 1, message: 'Must be 1 or greater' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const minSize = getFieldValue('minimum_serving_size');
                          if (!value || !minSize || value >= minSize) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Maximum must be >= minimum'));
                        }
                      })
                    ]} 
                    className="mb-0"
                  >
                    <InputNumber 
                      placeholder="0" 
                      size="large" 
                      className="w-32 h-11 rounded-lg" 
                      min={1} 
                    />
                  </Form.Item>
                </div>
              ) : (
                <Form.Item 
                  name="fixed_serving_size" 
                  rules={[
                    { required: true, message: 'Please enter serving size' },
                    { type: 'number', min: 1, message: 'Must be 1 or greater' }
                  ]} 
                  className="mb-0"
                >
                  <InputNumber 
                    placeholder="Enter serving size" 
                    size="large" 
                    className="w-full h-11 rounded-lg" 
                    min={1} 
                  />
                </Form.Item>
              )}
            </div>

            {/* Package Image */}
            <Form.Item label={<span className="text-gray-700 font-medium">Package Image</span>}>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-400 transition-colors bg-gray-50">
                <Upload 
                  name="image" 
                  listType="picture-card" 
                  showUploadList={false} 
                  beforeUpload={(file) => {
                    if (!file.type.startsWith('image/')) {
                      message.error('You can only upload image files!');
                      return Upload.LIST_IGNORE;
                    }
                    if (file.size / 1024 / 1024 >= 2) {
                      message.error('Image must be smaller than 2MB!');
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }} 
                  onChange={handleUploadChange}
                >
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt="package" 
                      className="w-20 h-20 rounded-lg object-cover" 
                    />
                  ) : (
                    <div>
                      <div className="text-4xl mb-2">📷</div>
                      <div className="text-sm text-gray-600">Click to Upload</div>
                      <div className="text-xs text-gray-500 mt-1">Max 2MB</div>
                    </div>
                  )}
                </Upload>
              </div>
            </Form.Item>
          </Form>
        </Spin>
      </div>

      {/* Fixed Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex justify-end gap-3">
          <Button 
            onClick={handleCancel} 
            size="large" 
            className="h-11 px-6 rounded-lg"
          >
            Cancel
          </Button>
          <Button 
            type="primary" 
            onClick={() => form.submit()} 
            size="large" 
            loading={isSubmitting || isLoading || uploading} 
            className="bg-orange-500 border-orange-500 hover:bg-orange-600 px-8 h-11 rounded-lg font-medium"
          >
            {isSubmitting || isLoading || uploading ? 'Updating...' : 'Update Package'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditEventModal;